import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EChartsOption } from 'echarts';

import { EChartsWrapper } from '@/features/dashboard-charts';
import { formatValue } from '@/entities/dashboard';
import { BREAKDOWN_TYPES, type BreakdownType } from '@/entities/dashboard/mapping';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { palette } from '@/shared/config/palette';

import { useLocationBreakdown } from '../model/use-location-breakdown';
import type { MapDataFilters } from '../model/map-provider.interface';

interface SliceTooltipParam {
  name?: string;
  value?: number;
  percent?: number;
}

const sliceTooltip = (params: unknown): string => {
  const slice = params as SliceTooltipParam;

  // Two decimals, as legacy prints its slices — a single one rounds 0.12 to 0.1
  // and 0.27 to 0.3, which reads as different data rather than the same number.
  return `${slice.name ?? ''}: ${formatValue(slice.value ?? 0, 'W', 2)} (${slice.percent ?? 0}%)`;
};

interface BreakdownChartProps {
  locationIds: string[];
  type: BreakdownType;
  dataFilters?: MapDataFilters;
}

function BreakdownChart({ locationIds, type, dataFilters }: BreakdownChartProps) {
  const { t } = useTranslation();
  const { pieData, isLoading } = useLocationBreakdown({ locationIds, type, dataFilters });

  const chartOption: EChartsOption = React.useMemo(
    () => ({
      tooltip: { trigger: 'item', formatter: sliceTooltip },
      series: [
        {
          type: 'pie',
          radius: ['25%', '50%'],
          center: ['50%', '50%'],
          itemStyle: { borderRadius: 3, borderColor: palette.white, borderWidth: 1 },
          label: { show: true, position: 'outside', fontSize: 9, formatter: '{b}: {d}%' },
          labelLine: { show: true, length: 5, length2: 3 },
          data: pieData.map((item) => ({ name: item.name, value: item.value, itemStyle: { color: item.color } }))
        }
      ]
    }),
    [pieData]
  );

  if (isLoading) {
    return <div className="rounded bg-muted p-4 text-center text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  if (pieData.length === 0) {
    return <div className="rounded bg-muted p-4 text-center text-sm text-muted-foreground">{t('common.noData')}</div>;
  }

  // The user guide lists the download options on this pie — it is the one chart
  // the mapping page exposes.
  return <EChartsWrapper option={chartOption} height={180} showExport />;
}

interface LocationPopupBreakdownProps {
  locationIds: string[];
  dataFilters?: MapDataFilters;
}

/** Legacy's two-tab pie on the Released Cargo slice — by product, then by user. */
export function LocationPopupBreakdown({ locationIds, dataFilters }: LocationPopupBreakdownProps) {
  const { t } = useTranslation();

  return (
    <Tabs defaultValue={BREAKDOWN_TYPES.BY_PRODUCT}>
      <TabsList>
        <TabsTrigger value={BREAKDOWN_TYPES.BY_PRODUCT}>{t('welcome.products.label')}</TabsTrigger>
        <TabsTrigger value={BREAKDOWN_TYPES.BY_ORGANIZATION}>{t('welcome.users.label')}</TabsTrigger>
      </TabsList>

      <TabsContent value={BREAKDOWN_TYPES.BY_PRODUCT}>
        <BreakdownChart locationIds={locationIds} type={BREAKDOWN_TYPES.BY_PRODUCT} dataFilters={dataFilters} />
      </TabsContent>
      <TabsContent value={BREAKDOWN_TYPES.BY_ORGANIZATION}>
        <BreakdownChart locationIds={locationIds} type={BREAKDOWN_TYPES.BY_ORGANIZATION} dataFilters={dataFilters} />
      </TabsContent>
    </Tabs>
  );
}
